import { useCallback } from 'react';
import { PAGE_NAME } from '../constants/analytics';
import { IAnalyticsEvent } from '../types/analytics';
import {
  apiException as formatApiException,
  event as formatEvent,
  exception as formatException,
  getRedactedTrackerFields,
  page as formatPage,
} from '../util/analytics';

// Read at call time: the GA snippet may load after this module, or be blocked entirely.
const getGa = (): UniversalAnalytics.ga | undefined => (typeof window.ga === 'function' ? window.ga : undefined);

// Refresh the tracker fields before every hit and repeat page and location on the hit itself,
// so no hit type falls back to the claim URL captured at ga('create').
const send = (hit: Record<string, unknown>) => {
  const ga = getGa();
  if (!ga) {
    return;
  }

  const fields = getRedactedTrackerFields();

  ga('set', fields);
  ga('send', { page: fields.page, ...hit, location: fields.location });
};

export const page = (pageName: string = PAGE_NAME) => send(formatPage(pageName));

export const event = (e: IAnalyticsEvent) => send(formatEvent(e));

export const exception = (error: any) => send(formatException(error));

export const apiException = (error: any) => send(formatApiException(error));

export const config = (c: { [k: string]: string }) => getGa()?.('set', c);

const useAnaytics = () => {
  const trackPage = useCallback(page, []);

  const trackEvent = useCallback(event, []);

  const trackException = useCallback(exception, []);

  const trackApiException = useCallback(apiException, []);

  const setConfig = useCallback(config, []);

  return {
    trackPage,
    trackEvent,
    trackException,
    trackApiException,
    setConfig,
  };
};

export default useAnaytics;
